/**
 * ErrorState Component
 * 
 * Reusable error state display for failed data fetching or operations.
 */

import { useTranslation } from '@/core';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/theme/components/ui/button';
import { cn } from '@/lib/utils';

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
  compact?: boolean;
}

export function ErrorState({
  title,
  message,
  onRetry,
  className,
  compact = false,
}: ErrorStateProps) {
  const { t } = useTranslation();
  
  const displayTitle = title ?? t('errors.loadingFailed');
  const displayMessage = message ?? t('errors.tryAgain');

  if (compact) {
    return (
      <div className={cn('flex items-center gap-2 text-destructive', className)}>
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm">{displayMessage}</span>
        {onRetry && (
          <Button variant="ghost" size="sm" onClick={onRetry} className="h-6 px-2">
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="h-6 w-6 text-destructive" />
      </div>
      <h3 className="mb-1 text-lg font-semibold text-foreground">{displayTitle}</h3>
      <p className="mb-4 max-w-sm text-sm text-muted-foreground">{displayMessage}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline" size="sm">
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

export default ErrorState;
